import { type HTMLAttributes, type KeyboardEvent, type ReactNode, useId, useRef } from 'react';
import { cn } from '../cn.ts';

export interface TabItem<T extends string = string> {
  value: T;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<T extends string = string> {
  items: readonly TabItem<T>[];
  value: T;
  onValueChange: (value: T) => void;
  /** Base id shared with <TabPanel idBase> so tabs and panels reference each other. */
  idBase?: string;
  'aria-label'?: string;
  className?: string;
}

const tabId = (base: string, value: string) => `${base}-tab-${value}`;
const panelId = (base: string, value: string) => `${base}-panel-${value}`;

/**
 * Segmented tabs like the dashboard's TabBar, with the WAI-ARIA tabs pattern: roving tabindex,
 * Arrow/Home/End to move, automatic activation.
 */
export function Tabs<T extends string>({
  items,
  value,
  onValueChange,
  idBase,
  className,
  ...aria
}: TabsProps<T>) {
  const generated = useId();
  const base = idBase ?? `tabs-${generated}`;
  const refs = useRef(new Map<string, HTMLButtonElement>());

  const enabled = items.filter((item) => !item.disabled);
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = enabled.findIndex((item) => item.value === value);
    const last = enabled.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? enabled[index >= last ? 0 : index + 1]
        : event.key === 'ArrowLeft'
          ? enabled[index <= 0 ? last : index - 1]
          : event.key === 'Home'
            ? enabled[0]
            : event.key === 'End'
              ? enabled[last]
              : undefined;
    if (!next) return;
    event.preventDefault();
    onValueChange(next.value);
    refs.current.get(next.value)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={aria['aria-label']}
      onKeyDown={move}
      className={cn(
        'inline-flex items-center h-8 p-0 gap-0.5 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 w-fit',
        className,
      )}
    >
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(node) => {
              if (node) refs.current.set(item.value, node);
              else refs.current.delete(item.value);
            }}
            type="button"
            role="tab"
            id={tabId(base, item.value)}
            aria-selected={selected}
            aria-controls={panelId(base, item.value)}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onValueChange(item.value)}
            className={cn(
              'relative h-8 px-4 truncate text-sm rounded-lg border-0 cursor-pointer transition-all duration-200 ease-out',
              'hover:text-n-brand disabled:cursor-not-allowed disabled:opacity-50',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand',
              selected
                ? 'bg-n-solid-active text-n-blue-11 shadow-sm outline outline-1 outline-n-container'
                : 'text-n-slate-10',
            )}
          >
            {item.label}
            {item.count !== undefined && ` (${item.count})`}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Same `idBase` as the <Tabs>. */
  idBase: string;
  value: string;
}

/** The panel for one tab; render only the active one. */
export function TabPanel({ idBase, value, className, ...rest }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={panelId(idBase, value)}
      aria-labelledby={tabId(idBase, value)}
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
      {...rest}
    />
  );
}
